import type {
  AttachmentSettingsObject,
  CardAttachment,
  CardColorType,
  GoogleClassroomClassSetting,
  GoogleClassroomPostType,
} from "@shared/models/card-stack"
import type { CardAttrsBase } from "@shared/unified-tiptap/util/card"
import { BLOCK_SEPARATOR, getSliceText } from "@shared/unified-tiptap/util/get-text"
import ObjectId from "@shared/util/object-id-gen"
import { mergeAttributes, Node, type Editor } from "@tiptap/core"
import { Fragment, Slice, type Node as PMNode } from "@tiptap/pm/model"
import { Plugin } from "@tiptap/pm/state"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { renderBooleanAsData, renderJsonAsData, renderStringAsData } from "../../util/render-data"
import type { ExtensionCardTitleAttrs } from "../card-title/card-title-extension"
import type { CardExtensionAttrs } from "../cards/card-extensions"
import type { Link } from "../link/link-attrs"
import { CardGoogleClassroomComponent } from "./card-google-classroom-component"

/**
 * Attrs mirror the legacy card-google-classroom attributes
 * (CardAttributesGoogleClassroom) one-to-one, so unified-migration.ts can convert back
 * and forth losslessly and the backend Google Classroom processor keeps working off the
 * derived legacy `cards`. Unlike titledCard — where the title and its color live on the
 * cardTitle child — everything lives here: the node is an atom with no children, and
 * its content is rendered directly by the ReactNodeView.
 */
export interface ExtensionCardGoogleClassroomAttrs extends CardAttrsBase {
  color: CardColorType
  isPublic: boolean
  postType: GoogleClassroomPostType
  title: string
  value: string
  maxPoints: string | null
  classSettings: Array<GoogleClassroomClassSetting>
  attachmentSettings: AttachmentSettingsObject
  extraPostProperties: {
    topicName?: string | null
    choices?: Array<string> | null
  }
  enabledGoogleClassIds: Array<string>
  attachments: Array<CardAttachment>
  /** The legacy `_revision`: bumped on edit so the backend knows to update the GC post. */
  revision: number
}

/**
 * A Google Classroom card. Mostly non-interactive in the editor itself: the node view
 * displays the post (title, value, attachments) and clicking it opens the Google
 * Classroom sidebar, where the actual editing happens.
 *
 * Group "card" makes it a sibling of titledCard at the lesson level; "widget" opts into
 * the editor's size-1 ReactNodeView handling (click overrides, comment decorations —
 * see CLAUDE.md), though unlike other widgets it is not in group "block", so it can
 * never appear inside a card.
 */
export const ExtensionCardGoogleClassroom = Node.create({
  name: "cardGoogleClassroom",
  group: "card addTarget widget",
  atom: true,

  draggable: true,

  addAttributes() {
    return {
      id: { ...renderStringAsData("id"), isRequired: true },
      parentCardId: { ...renderStringAsData("parentCardId", "parent-card-id"), default: null },
      commentIds: {
        ...renderJsonAsData("commentIds"),
        default: [],
      },
      color: { ...renderStringAsData("color"), default: "yellow" },
      isPublic: { ...renderBooleanAsData("isPublic", "is-public"), default: false },
      postType: { ...renderStringAsData("postType", "post-type"), default: "ASSIGNMENT" },
      title: { ...renderStringAsData("title"), default: "" },
      value: { ...renderStringAsData("value"), default: "" },
      maxPoints: { ...renderStringAsData("maxPoints", "max-points"), default: null },
      classSettings: { ...renderJsonAsData("classSettings", "class-settings"), default: [] },
      attachmentSettings: {
        ...renderJsonAsData("attachmentSettings", "attachment-settings"),
        default: {},
      },
      extraPostProperties: {
        ...renderJsonAsData("extraPostProperties", "extra-post-properties"),
        default: {},
      },
      enabledGoogleClassIds: {
        ...renderJsonAsData("enabledGoogleClassIds", "enabled-google-class-ids"),
        default: [],
      },
      attachments: { ...renderJsonAsData("attachments", "attachments"), default: [] },
      revision: {
        default: 0,
        renderHTML: (attributes) => ({ "data-revision": String(attributes.revision) }),
        parseHTML: (element) => Number(element.getAttribute("data-revision") ?? 0),
      },
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as ExtensionCardGoogleClassroomAttrs
    // A readable fallback rendering (clipboard / external paste targets); the editor
    // itself always uses the NodeView.
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-content-type": "cardGoogleClassroom" }),
      ["h2", {}, attrs.title ?? ""],
      ["p", {}, attrs.value ?? ""],
    ]
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-content-type="cardGoogleClassroom"]',
      },
    ]
  },

  // Plain-text rendering (getText and the clipboard's text/plain flavor); without
  // this, the card contributes nothing since all its data lives in attrs.
  renderText({ node }) {
    const attrs = node.attrs as ExtensionCardGoogleClassroomAttrs
    return [attrs.title, attrs.value].filter(Boolean).join(BLOCK_SEPARATOR)
  },

  addNodeView() {
    return ReactNodeViewRenderer(CardGoogleClassroomComponent)
  },

  addProseMirrorPlugins() {
    return [new Plugin({ props: { transformPasted } })]
  },
})

/**
 * The Google Classroom post types a card can become, in menu order.
 */
export const GOOGLE_CLASSROOM_POST_TYPES: ReadonlyArray<{
  postType: GoogleClassroomPostType
  label: string
}> = [
  { postType: "ASSIGNMENT", label: "Assignment" },
  { postType: "SHORT_ANSWER_QUESTION", label: "Short Answer Question" },
  { postType: "MULTIPLE_CHOICE_QUESTION", label: "Multiple Choice Question" },
  { postType: "MATERIAL", label: "Material" },
  { postType: "ANNOUNCEMENT", label: "Announcement" },
]

/**
 * The Google Classroom card a titledCard becomes when posted (see `postCardToGoogleClassroom`).
 *
 * The card keeps everything it already had — its id (comments and the backend's Google
 * Classroom post are keyed to it), title, comment ids, color, and public flag — while the
 * post-specific attrs start at the post type's defaults. A Google Classroom post holds
 * plaintext and a list of attachments, so the card's block content is flattened: the
 * attachment tokens (linkBlocks) become `attachments`, and what is left becomes `value`.
 */
export function googleClassroomCardFromTitledCard(
  editor: Editor,
  card: PMNode,
  postType: GoogleClassroomPostType,
): PMNode {
  const cardAttrs = card.attrs as CardExtensionAttrs
  const title = card.firstChild
  const titleAttrs = title?.attrs as ExtensionCardTitleAttrs | undefined

  const content = card.children.slice(1)
  const attachments = content
    .filter((node) => node.type.name === "linkBlock")
    .map((node) => cardAttachmentFromLink(node.attrs as Link))
  const valueContent = content.filter((node) => node.type.name !== "linkBlock")

  return editor.schema.node("cardGoogleClassroom", {
    ...cardAttrs,
    color: titleAttrs?.color ?? "yellow",
    postType,
    title: title?.textContent ?? "",
    value: getSliceText(editor, new Slice(Fragment.fromArray(valueContent), 0, 0)),
    maxPoints: null,
    classSettings: [],
    attachmentSettings: {},
    // Multiple choice can't be posted without at least one choice, so seed one.
    extraPostProperties: postType === "MULTIPLE_CHOICE_QUESTION" ? { choices: ["Option 1"] } : {},
    enabledGoogleClassIds: [],
    attachments,
    revision: 0,
  } satisfies ExtensionCardGoogleClassroomAttrs)
}

/**
 * Converts unified Link attrs back to the legacy CardAttachment shape (the reverse of
 * the docs/unified-links.md §10 mapping): attachmentId → id, minting one if absent.
 */
export function cardAttachmentFromLink(link: Link): CardAttachment {
  return {
    id: link.attachmentId ?? ObjectId.create(),
    title: link.title ?? link.url,
    url: link.url,
    type: link.type,
    mimetype: link.mimetype,
    size: link.size,
    isWriteable: link.isWriteable,
  }
}

/**
 * When pasting cardGoogleClassroom cards, assign each a fresh id and store the original
 * as parentCardId.
 * Also erase commentIds, to avoid duplicating comments.
 * Cf. card-extensions' transformPasted.
 */
function transformPasted(slice: Slice): Slice {
  const replaceCards = (fragment: Fragment): Fragment => {
    const children: ReturnType<typeof fragment.child>[] = []
    for (let i = 0; i < fragment.childCount; i++) {
      const node = fragment.child(i)
      if (node.type.name === "cardGoogleClassroom") {
        const attrs = node.attrs as ExtensionCardGoogleClassroomAttrs
        const newAttrs = {
          ...attrs,
          id: ObjectId.create(),
          parentCardId: attrs.id,
          commentIds: [],
        } satisfies ExtensionCardGoogleClassroomAttrs
        children.push(node.type.create(newAttrs, replaceCards(node.content), node.marks))
      } else {
        children.push(node.copy(replaceCards(node.content)))
      }
    }
    return Fragment.fromArray(children)
  }

  return new Slice(replaceCards(slice.content), slice.openStart, slice.openEnd)
}
